<template>
  <page-section>
    <rest-list-editable
      :payload="fetchEndpoint"
      ref="rest-list-editable"
      :add-endpoint="rowAddEndpoint"
      :update-endpoint="rowUpdateEndpoint"
    >
      <rest-list-column
        prop="id"
        :title="$t('Integrations_PromotionsList_Field_Id')"
        :width="100"
      />

      <rest-list-column
        prop="external_id"
        :title="$t('Integrations_PromotionsList_Field_External_Id')"
        :editable="true"
        :width="200"
      />

      <rest-list-column
        prop="name"
        :title="$t('Integrations_PromotionsList_Field_Name')"
        :editable="true"
      />

      <rest-list-column
        prop="description"
        :title="$t('Integrations_PromotionsList_Field_Description')"
        :editable="true"
      />

      <rest-list-column-boolean
        prop="active"
        :title="$t('Integrations_PromotionsList_Field_Active')"
        :editable="true"
        :width="200"
      />

      <rest-list-column-boolean
        prop="advanced"
        :title="$t('Integrations_PromotionsList_Field_Advanced')"
        :editable="true"
        :only-on-create="true"
        :sortable="false"
        :filterable="false"
        :width="areMetadataAndParamsVisible ? 100 : 200"
        v-if="syncDisabled"
        class-name="editable-row-advanced"
      />

      <rest-list-column-boolean
        prop="amount_required"
        :title="$t('Integrations_PromotionsList_Field_Amount_Required')"
        :editable="true"
        :width="areMetadataAndParamsVisible ? 100 : 200"
        v-if="!syncAdvanced"
      />

      <rest-list-column-boolean
        prop="duration_required"
        :title="$t('Integrations_PromotionsList_Field_Duration_Required')"
        :editable="true"
        :width="areMetadataAndParamsVisible ? 100 : 200"
        v-if="!syncAdvanced"
      />

      <rest-list-column
        prop="metadata"
        :title="metadataTitle"
        :editable="false"
        :sortable="false"
        :width="130"
        v-if="areMetadataAndParamsVisible"
      >
        <template slot-scope="scope">
          <el-button
            size="mini"
            @click="
              showDataPopup(metadataTitle, JSON.stringify(scope.row.metadata))
            "
            v-if="scope.row.metadata"
          >
            <el-icon name="view" />
          </el-button>
          <el-button
            size="mini"
            v-if="syncDisabled && scope.row.advanced === 1"
            @click="showMetadataPopup(scope.row.id, scope.row.metadata)"
          >
            <el-icon name="edit" />
          </el-button>
        </template>
      </rest-list-column>

      <rest-list-column
        prop="params"
        :title="paramsTitle"
        :editable="false"
        :sortable="false"
        :width="130"
        v-if="areMetadataAndParamsVisible"
      >
        <template slot-scope="scope">
          <el-button
            size="mini"
            @click="
              showDataPopup(paramsTitle, JSON.stringify(scope.row.params))
            "
            v-if="scope.row.params"
          >
            <el-icon name="view" />
          </el-button>
          <el-button
            size="mini"
            v-if="syncDisabled && scope.row.advanced === 1"
            @click="showParamsPopup(scope.row.id, scope.row.params)"
          >
            <el-icon name="edit" />
          </el-button>
        </template>
      </rest-list-column>

      <el-table-column
        :width="50"
        align="center"
        class-name="editable-row-actions"
        :fixed="false"
      >
        <template v-slot="scope" v-if="!syncEnabled || syncDisabled">
          <rest-list-action-edit :index="scope.$index" icon="el-icon-more">
            <rest-list-action-delete
              :endpoint="rowDeleteEndpoint(scope.row)"
              :name="scope.row.name"
            />
          </rest-list-action-edit>
        </template>
      </el-table-column>
    </rest-list-editable>

    <el-dialog
      v-if="dataPopup"
      :title="dataPopup"
      :visible="dataPopup && dataPopup.length > 0"
      :modal="false"
      :modal-append-to-body="false"
      :before-close="closeDataPopup"
    >
      <vue-json-pretty :data="jsonData" class="json-wrapper"></vue-json-pretty>
    </el-dialog>

    <el-dialog
      v-if="editParams"
      :title="paramsTitle"
      :visible="editParams"
      :modal="false"
      :modal-append-to-body="false"
      :before-close="closeParamsEdit"
    >
      <plain-json-editor v-model="jsonData" ref="paramsEditor" />
      <span slot="footer" class="dialog-footer">
        <el-button @click="closeParamsEdit">Cancel</el-button>
        <el-button @click="saveJsonParam('params')" type="primary"
          >Save</el-button
        >
      </span>
    </el-dialog>

    <el-dialog
      v-if="editMetadata"
      :title="metadataTitle"
      :visible="editMetadata"
      :modal="false"
      :modal-append-to-body="false"
      :before-close="closeMetadataEdit"
    >
      <plain-json-editor v-model="jsonData" ref="metadataEditor" />
      <span slot="footer" class="dialog-footer">
        <el-button @click="closeMetadataEdit">Cancel</el-button>
        <el-button @click="saveJsonParam('metadata')" type="primary"
          >Save</el-button
        >
      </span>
    </el-dialog>
  </page-section>
</template>

<script>
import PlainJsonEditor from '@/common/components/PlainJsonEditor'
import RestListEditable from '@/common/components/rest/RestListEditable'
import RestListColumn from '@/common/components/rest/RestListColumn'
import RestListColumnBoolean from '@/common/components/rest/RestListColumnBoolean'
import RestListActionEdit from '@/common/components/rest/RestListActionEdit'
import RestListActionDelete from '@/common/components/rest/RestListActionDelete'
import { projectEndpoint } from '@/core/helpers'
import PageSection from '@/common/components/page/PageSection'
import VueJsonPretty from 'vue-json-pretty'
import { EventBus } from '@/core/EventBus'
import RestApi from '@/core/services/RestApi'

export default {
  name: 'IntegrationPromotionsTable',

  components: {
    RestListColumnBoolean,
    PageSection,
    RestListEditable,
    RestListColumn,
    RestListActionEdit,
    RestListActionDelete,
    VueJsonPretty,
    PlainJsonEditor
  },

  props: {
    integrationId: {
      type: Number
    },

    integrationType: {
      type: String
    },

    syncEnabled: {
      type: Boolean,
      default: true
    },

    syncAdvanced: {
      type: Boolean,
      default: false
    },

    syncDisabled: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      dataPopup: false,
      jsonData: null,
      editParams: false,
      editMetadata: false,
      editingRow: null
    }
  },

  created() {
    EventBus.$on('add-row-requested', this.onAddRow)
  },

  beforeDestroy() {
    EventBus.$off('add-row-requested', this.onAddRow)
  },

  computed: {
    fetchEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `promotions`,
        params: {
          sort: '-create_time',
          integration_id: this.integrationId
        }
      })
    },

    rowAddEndpoint() {
      return projectEndpoint({
        method: 'post',
        url: `promotions`
      })
    },

    paramsTitle() {
      return this.$t('Integrations_PromotionsList_Field_Params')
    },

    metadataTitle() {
      return this.$t('Integrations_PromotionsList_Field_Metadata')
    },

    areMetadataAndParamsVisible() {
      return this.syncAdvanced || this.syncDisabled
    }
  },

  methods: {
    rowDeleteEndpoint(row) {
      return projectEndpoint({
        method: 'delete',
        url: `promotions/${row.id}`
      })
    },

    rowUpdateEndpoint(row) {
      return projectEndpoint({
        method: 'patch',
        url: `promotions/${row.id}`
      })
    },

    saveJsonParam(param) {
      const sendData = {}
      sendData[param] = this.minifyJson(this.jsonData)
      RestApi(
        projectEndpoint({
          method: 'patch',
          url: `promotions/${this.editingRow}`,
          data: sendData
        })
      )
        .then(({ data }) => {
          this.closeMetadataEdit()
          this.closeParamsEdit()
          this.$refs['rest-list-editable'].$refs['restList'].refresh()
        })
        .catch(error => {
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },

    onAddRow() {
      this.$refs['rest-list-editable'].addRow({
        integration_id: this.integrationId
      })
    },

    showDataPopup(title, json) {
      this.jsonData = JSON.parse(json)
      this.dataPopup = title
    },

    showParamsPopup(id, json) {
      this.editingRow = id
      this.jsonData = this.formatJson(json)
      this.editParams = true
    },

    showMetadataPopup(id, json) {
      this.editingRow = id
      this.jsonData = this.formatJson(json)
      this.editMetadata = true
    },

    closeDataPopup() {
      this.dataPopup = false
    },

    closeParamsEdit() {
      this.editingRow = null
      this.editParams = false
      this.jsonData = null
    },

    closeMetadataEdit() {
      this.editingRow = null
      this.editMetadata = false
      this.jsonData = null
    },

    formatJson(jsonString) {
      if (!jsonString) {
        return '{}'
      }
      try {
        const obj = JSON.parse(JSON.stringify(jsonString))
        return JSON.stringify(obj, null, 2)
      } catch (e) {
        return jsonString
      }
    },

    minifyJson(jsonString) {
      try {
        const obj = JSON.parse(jsonString)
        return JSON.stringify(obj)
      } catch (e) {
        return jsonString
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/modules/integrations/components/IntegrationPromotionsTable.vue